/**
 * Case Pack Enforcer v3
 * =====================
 * Patches the Trade theme's QuantityInput Web Component prototype so every
 * quantity input on the site respects case pack sizes from product tags.
 *
 * Pack size is read from the nearest ancestor (or self) with data-case-pack
 * containing a comma-separated list of product tags.
 *
 * Supported locations:
 *  - Product page       → data-case-pack on <product-info> (main-product.liquid)
 *  - Cart page          → data-case-pack on <quantity-input> (main-cart-items.liquid)
 *  - Cart drawer        → data-case-pack on <quantity-input> (if cart drawer present)
 *  - Collection cards   → data-case-pack set by case-pack-tagger.liquid snippet
 *  - Quick order list   → data-case-pack set by case-pack-tagger.liquid snippet
 *
 * Tag format: "case-of-3" or "case-of-4" or "case-of-6" anywhere in product tags.
 */

(function () {
  'use strict';

  // ─── Config: add more pack sizes here if needed ───────────────────────────
  const CASE_PACK_TAG_MAP = {
    'case-of-3': 3,
    'case-of-4': 4,
    'case-of-6': 6,
    'case-of-8': 8,
    'case-of-12': 12,
  
  };
  // ──────────────────────────────────────────────────────────────────────────

  /** Parse a comma-separated tags string → pack size integer or null */
  function getPackSize(tagsString) {
    if (!tagsString) return null;
    const tags = tagsString.split(',').map((t) => t.trim().toLowerCase());
    for (const [tag, size] of Object.entries(CASE_PACK_TAG_MAP)) {
      if (tags.includes(tag)) return size;
    }
    return null;
  }

  /**
   * Walk up the DOM from el looking for data-case-pack.
   * Checks the element itself first (handles cart <quantity-input> case),
   * then walks up ancestors.
   */
  function findPackSizeForElement(el) {
    let node = el;
    while (node && node !== document.documentElement) {
      if (node.dataset && node.dataset.casePack) {
        const size = getPackSize(node.dataset.casePack);
        if (size) return size;
      }
      node = node.parentElement;
    }
    return null;
  }

  /**
   * Snap value UP to nearest valid multiple of step, with min as floor.
   * If value is 0 (cart "remove" threshold), leave it — cart handles 0 as removal.
   */
  function snapUp(value, step, min) {
    if (value === 0) return 0; // allow 0 for cart item removal
    if (value <= min) return min;
    const remainder = (value - min) % step;
    return remainder === 0 ? value : value + (step - remainder);
  }

  /**
   * Apply pack size attributes and snap value for a single quantity-input element.
   * Called both from the patched connectedCallback and from the MutationObserver.
   */
  function initQuantityInput(qtyEl) {
    const packSize = findPackSizeForElement(qtyEl);
    if (!packSize) return;

    const input = qtyEl.querySelector('input.quantity__input');
    if (!input) return;

    // Store on the element for fast access in patched methods
    qtyEl._casePackSize = packSize;

    // Update input attributes
    input.setAttribute('step', packSize);
    input.setAttribute('data-min', packSize);

    // For cart items, min stays 0 (allows removal by typing 0).
    // For product page / quick order, min should be the pack size.
    const isCartInput = input.name === 'updates[]' ||
      !!qtyEl.closest('cart-items, cart-drawer-items');
    if (!isCartInput) {
      input.setAttribute('min', packSize);
    }

    // Snap current value (leave 0 alone — cart removal)
    const current = parseInt(input.value) || 0;
    const snapped = snapUp(current, packSize, packSize);
    if (current !== snapped && current !== 0) {
      input.value = snapped;
    }
  }

  /**
   * Patch the QuantityInput prototype once the custom element is defined.
   * By patching the prototype, ALL instances automatically get the behaviour —
   * including ones injected later by AJAX (cart drawer, quick order list, etc.)
   */
  function patchQuantityInputPrototype() {
    const ctor = customElements.get('quantity-input');
    if (!ctor) {
      console.warn('[CasePack] quantity-input custom element not found.');
      return;
    }

    const proto = ctor.prototype;

    // ── Patch: connectedCallback ─────────────────────────────────────────────
    // Runs when the element is inserted into the DOM. We init after the theme's
    // own connectedCallback settles (microtask via Promise.resolve).
    const originalConnectedCallback = proto.connectedCallback;
    proto.connectedCallback = function () {
      originalConnectedCallback.call(this);
      Promise.resolve().then(() => initQuantityInput(this));
    };

    // ── Patch: onButtonClick ─────────────────────────────────────────────────
    // Replaces stepUp/stepDown with pack-size-aware increment/decrement.
    const originalOnButtonClick = proto.onButtonClick;
    proto.onButtonClick = function (event) {
      const packSize = this._casePackSize || findPackSizeForElement(this);
      if (!packSize) {
        return originalOnButtonClick.call(this, event);
      }

      event.preventDefault();
      const previousValue = this.input.value;
      const current = parseInt(this.input.value) || 0;
      const isCartInput = this.input.name === 'updates[]' ||
        !!this.closest('cart-items, cart-drawer-items');
      const max = parseInt(this.input.getAttribute('max')) || Infinity;

      if (event.target.name === 'plus') {
        // If currently 0 (empty cart state), jump straight to packSize
        this.input.value = current === 0
          ? packSize
          : Math.min(max, current + packSize);
      } else {
        const next = current - packSize;
        // Cart: allow going to 0 (triggers line item removal).
        // Product page: floor at packSize.
        this.input.value = isCartInput
          ? Math.max(0, next)
          : Math.max(packSize, next);
      }

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(this.changeEvent);
      }
    };

    // ── Patch: onInputChange (fired by the theme on the 'change' event) ──────
    const originalOnInputChange = proto.onInputChange;
    proto.onInputChange = function (event) {
      const packSize = this._casePackSize || findPackSizeForElement(this);
      if (packSize) {
        const val = parseInt(this.input.value);
        // Allow 0 in cart (removal), otherwise snap up to nearest multiple
        if (!isNaN(val) && val !== 0) {
          const snapped = snapUp(val, packSize, packSize);
          if (val !== snapped) {
            this.input.value = snapped;
          }
        }
      }
      originalOnInputChange.call(this, event);
    };

    // ── Patch: validateQtyRules ───────────────────────────────────────────────
    // Original checks input.min to decide whether to disable the minus button.
    // We ensure step/min reflect packSize before calling original.
    const originalValidateQtyRules = proto.validateQtyRules;
    proto.validateQtyRules = function () {
      const packSize = this._casePackSize || findPackSizeForElement(this);
      if (packSize) {
        this.input.step = packSize;
        const isCartInput = this.input.name === 'updates[]' ||
          !!this.closest('cart-items, cart-drawer-items');
        if (!isCartInput) {
          this.input.min = packSize;
        }
      }
      originalValidateQtyRules.call(this);
    };
  }

  /**
   * Add a blur listener (capture phase) to snap manually typed values.
   * The theme's onInputChange fires on 'change' which can lag behind typing.
   * Blur fires immediately when the user tabs or clicks away.
   */
  function addBlurSnapListener() {
    document.addEventListener('blur', (e) => {
      if (!e.target.classList.contains('quantity__input')) return;
      const qtyEl = e.target.closest('quantity-input');
      if (!qtyEl) return;
      const packSize = qtyEl._casePackSize || findPackSizeForElement(qtyEl);
      if (!packSize) return;

      const val = parseInt(e.target.value);
      if (isNaN(val) || val === 0) return; // leave 0 for cart removal

      const snapped = snapUp(val, packSize, packSize);
      if (val !== snapped) {
        e.target.value = snapped;
        // Dispatch both input and change so the cart/product JS picks it up
        e.target.dispatchEvent(new Event('input', { bubbles: true }));
        e.target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, true); // capture phase
  }

  /** Scan all existing quantity-inputs on the page and init them */
  function initAllExisting() {
    document.querySelectorAll('quantity-input').forEach((el) => {
      initQuantityInput(el);
    });
  }

  /**
   * Watch for dynamically injected quantity-inputs:
   * cart drawer updates, quick order list AJAX rows, quick-add modals, etc.
   */
  function observeDOMForNewInputs() {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'QUANTITY-INPUT') {
            setTimeout(() => initQuantityInput(node), 0);
          }
          if (node.querySelectorAll) {
            node.querySelectorAll('quantity-input').forEach((el) => {
              setTimeout(() => initQuantityInput(el), 0);
            });
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ── Boot ──────────────────────────────────────────────────────────────────
  customElements.whenDefined('quantity-input').then(() => {
    patchQuantityInputPrototype();
    initAllExisting();
    observeDOMForNewInputs();
    addBlurSnapListener();
    console.log('[CasePack] Enforcer v3 active');
  });

})();